var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _time, _scene, _cycleMgr, _handMovement, _dwellF, _shiftF, _cycleDuration, _gravity;
import { isTrajectoryHolding, } from "./pattern/state";
import { Parser } from "./pattern/parser";
import { Scene } from "./scene";
import { StateCycleManager } from "./statecyclemgr";
import { parseHandSequence, DefaultHandSequenceName, HandSequenceMap, SequenceHandMovement, } from "./handmovement";
import { Vec3D } from "./vec3d";
import { BBox3D } from "./bbox3d";
import { TossPath, HandSpline } from "./path";
import { loopFinder } from "./loopfinder";
import { PlayerTime } from "./playertime";
/*

Given:
- full cycle, T=[0,1) containing [catch-throw)
- D=[0,1) = dwell time, time between throw and catch
- S=[0,1) = shift time, time shift left vs right hand

If synchronous pattern:

    T=0: Catch
    T=D: throw
    T=1: Catch 2
    ...

If async:

    LEADING / LEFT
    T=0: Catch
    T=D: throw
    T=1: Catch 2
    ...

    FOLLOWING / RIGHT
    T=S: Catch
    T=S+D: throw
    T=S+1: Catch 2
    ...

Our state machine performs per step:

    CATCH+THROW

So:

    STATE0--(CATCH+THROW)-->STATE1--(CATCH+THROW)-->STATE2

Question: given T=t, what does the scene look like?
Assume: t inside [ts, ts+1)

There are 3 states in there:

ts_state_start (initial), ts_state_mid (initial--CATCH+THROW-->), ts_start_end (mid--initial-->CATCH+THROW-->)

Given ts_state_start, juggler # and hand #:
- Determine if hand LEADS or FOLLOWS.

If LEADS:

- For t in [ts, ts+D): hand moving from caught to throw   (cycle #t)
- For t in [ts+D, ts+1): hand moving from throw to caught (cycle #t)

If FOLLOWS:

- For t in [ts, ts+S): hand moving from throw to caught] (cycle #t-1)
- For t in [ts+S, ts+S+D) or [ts+S+D, ts+1): hand caught to throw (cycle #t)
- For t in [ts, ts+S) or [ts+S+D, ts+1): hand moving from throw to caught] (cycle #t)

Where are the props? Notes about state:

- Prop, height 1 that was just thrown is in catch queue at position 0
- Any prop in the air is somewhere in the catch queue.
- All those props have a trajectory, containing both source and target hand.
- State generation at which thrown is recorded, so is height.
- So, state generation at which it lands is known.
- E.g. 3 thrown at G=g from hand L is at catch queue position 2 when thrown, at g+1 at 1, ...
- Generation: thrown at even generation are leading throws, odd following throws.

Given a trajectory, thrown at gen GT, lands at GC
- Let C=cycle(GT)
- Let T=C  (time of start of cycle)
- If GT is even, it was a leading throw, meaning, ball starts at T+D
- If GT is odd, it was a following throw, meaning, ball starts at T+S+D

Ends at:
- Let C=cycle(GC)
- Let T=C (time of start of cycle)
- If GC is even, LEAD, so catch at T
- If GC is odd, FOLLOWING, so catch at T + S

*/
export class Player {
    constructor(p, cycleDuration) {
        _time.set(this, new PlayerTime());
        _scene.set(this, void 0);
        _cycleMgr.set(this, void 0);
        _handMovement.set(this, void 0);
        _dwellF.set(this, 0.5);
        _shiftF.set(this, 0.5);
        _cycleDuration.set(this, void 0);
        _gravity.set(this, void 0);
        __classPrivateFieldSet(this, _cycleDuration, cycleDuration);
        const jugglerCount = p.patterns.length;
        __classPrivateFieldSet(this, _scene, new Scene(jugglerCount));
        __classPrivateFieldSet(this, _handMovement, new SequenceHandMovement(parseHandSequence(HandSequenceMap[DefaultHandSequenceName])));
        __classPrivateFieldSet(this, _cycleMgr, new StateCycleManager(p));
        // const Gravity = 0.00098; // cm per millisquared
        __classPrivateFieldSet(this, _gravity, (9.8 * 100) / (1000 * 1000));
    }
    setTimeScale(s) {
        __classPrivateFieldGet(this, _time).setTimeScale(s);
    }
    /* Throws of height 1 put in special constraints. Consider:
     *
     *     S=0.5, D=0.5.
     *
     * Then, a leading throw at T=t would have to be caught at:
     *
     *     T=t + S - D=t
     *
     * So, it follows that:
     *
     *     D<S.
     *
     * Additionally, a following throw at T=t+S would have to be caught at:
     *
     * T=t+1-D
     * t+S < t+1-D
     * D < 1-S
     */
    setCadence(dwellF, shiftF) {
        __classPrivateFieldSet(this, _dwellF, dwellF);
        __classPrivateFieldSet(this, _shiftF, shiftF);
    }
    setHandSequence(seq) {
        __classPrivateFieldSet(this, _handMovement, new SequenceHandMovement(seq));
    }
    cycleTToMillis(cycle, t) {
        return (cycle + t) * __classPrivateFieldGet(this, _cycleDuration);
    }
    setPaused(paused) {
        __classPrivateFieldGet(this, _time).setPaused(paused);
    }
    timeToCycleT(timestamp) {
        const cycle = Math.floor(timestamp / __classPrivateFieldGet(this, _cycleDuration));
        const t = (timestamp % __classPrivateFieldGet(this, _cycleDuration)) / __classPrivateFieldGet(this, _cycleDuration);
        return [cycle, t];
    }
    beat(timestamp) {
        timestamp = __classPrivateFieldGet(this, _time).wallClockBeat(timestamp);
        return this.positionScene(timestamp);
    }
    positionScene(timestamp) {
        const [cycle, t] = this.timeToCycleT(timestamp);
        const states = __classPrivateFieldGet(this, _cycleMgr).getStatesAtCycle(cycle);
        const state = states[0];
        state.jugglers.forEach((jstate, j) => {
            jstate.hands.forEach((hstate, h) => {
                const pos = this.getHandPosition(cycle, t, state, j, h);
                __classPrivateFieldGet(this, _scene).setHandPosition(j, h, pos);
            });
        });
        __classPrivateFieldGet(this, _scene).positionJugglers();
        this.positionProps(cycle, t, states);
        return __classPrivateFieldGet(this, _scene);
    }
    getBoundingBox() {
        let box = new BBox3D();
        const maxTime = (__classPrivateFieldGet(this, _cycleDuration) * __classPrivateFieldGet(this, _cycleMgr).states.length) / 2;
        for (let time = 0; time < maxTime; time += __classPrivateFieldGet(this, _cycleDuration) / 4) {
            const scene = this.positionScene(time);
            const sbox = scene.getBoundingBox();
            box.merge(sbox);
        }
        return box;
    }
    isHandLeading(state, j, h) {
        return state.gen === state.jugglers[j].hands[h].lastThrowGen;
    }
    wasHandLeadingAtGen(throwGen) {
        return throwGen % 2 === 0;
    }
    positionProps(cycle, t, states) {
        __classPrivateFieldGet(this, _scene).clearProps();
        const millis = this.cycleTToMillis(cycle, t);
        const propTracker = {};
        // Go back in time until all props have been tracked down.
        let untrackedPropCount = states[0].props.nextId;
        for (let gen = cycle * 2 + 1; untrackedPropCount > 0; gen--) {
            const state = __classPrivateFieldGet(this, _cycleMgr).getStateAtGen(gen);
            for (const propIdAny of Object.keys(state.props.props)) {
                const propId = propIdAny;
                let pt = propTracker[propId];
                if (!pt) {
                    pt = {
                        curPath: null,
                        prevTrajectory: null,
                        prevPath: null,
                    };
                    propTracker[propId] = pt;
                }
                if (pt.curPath) {
                    continue;
                }
                const propState = state.props.props[propId];
                if (!propState.trajectory) {
                    if (!propState.heldBy) {
                        throw new Error();
                    }
                    continue;
                }
                const path = this.getTrajectoryPath(propState.trajectory);
                if (path.containsT(millis)) {
                    if (isTrajectoryHolding(propState.trajectory)) {
                        __classPrivateFieldGet(this, _scene).holdProp(propState.trajectory.target.juggler, propState.trajectory.target.hand, propId);
                    }
                    else {
                        __classPrivateFieldGet(this, _scene).propAirborn(propId, path, millis);
                    }
                    pt.curPath = path;
                    untrackedPropCount--;
                }
                if (path &&
                    millis >= path.endT &&
                    (!pt.prevPath || millis - path.endT < millis - pt.prevPath.endT)) {
                    if (!pt.prevPath) {
                        untrackedPropCount--;
                    }
                    pt.prevPath = path;
                    pt.prevTrajectory = propState.trajectory;
                }
            }
        }
        for (const propIdAny of Object.keys(propTracker)) {
            const propId = propIdAny;
            const track = propTracker[propId];
            if (!track.curPath && track.prevTrajectory) {
                __classPrivateFieldGet(this, _scene).holdProp(track.prevTrajectory.target.juggler, track.prevTrajectory.target.hand, propId);
            }
        }
        __classPrivateFieldGet(this, _scene).finalizeProps();
    }
    getTrajectoryPath(trajectory) {
        const [sourcePos, sourceMillis] = this.getAbsHandPosition(trajectory.thrownAtGen, trajectory.source.juggler, trajectory.source.hand, true);
        const [targetPos, targetMillis] = this.getAbsHandPosition(trajectory.thrownAtGen + trajectory.height, trajectory.target.juggler, trajectory.target.hand, false);
        return new TossPath(__classPrivateFieldGet(this, _gravity), sourceMillis, sourcePos, targetMillis, targetPos);
    }
    getHandVelocity(gen, j, h, thr0w) {
        let ret = new Vec3D();
        let state;
        if (thr0w) {
            state = __classPrivateFieldGet(this, _cycleMgr).getStateAtGen(gen);
        }
        else {
            if (gen < 1) {
                return ret;
            }
            state = __classPrivateFieldGet(this, _cycleMgr).getStateAtGen(gen - 1);
        }
        let maxTrajectory = null;
        for (const propIdAny of Object.keys(state.props.props)) {
            const propId = propIdAny;
            const propState = state.props.props[propId];
            const trajectory = propState.trajectory;
            if (!trajectory) {
                continue;
            }
            let take = false;
            if (thr0w) {
                take =
                    trajectory.thrownAtGen === gen &&
                        trajectory.source.juggler === j &&
                        trajectory.source.hand === h;
            }
            else {
                take =
                    trajectory.thrownAtGen + trajectory.height === gen &&
                        trajectory.target.juggler === j &&
                        trajectory.target.hand === h;
            }
            if (take &&
                (!maxTrajectory || maxTrajectory.height < trajectory.height)) {
                maxTrajectory = trajectory;
            }
        }
        if (maxTrajectory) {
            const path = this.getTrajectoryPath(maxTrajectory);
            ret = path.velocityAt(thr0w ? path.startT : path.endT);
        }
        return ret;
    }
    getHandPosition(cycle, t, state0, j, h) {
        const leading = this.isHandLeading(state0, j, h);
        let sourceT = 0, targetT = 0, sourceGen;
        let gen = state0.gen;
        let sourceThrow = true;
        if (leading) {
            if (t < 1 - __classPrivateFieldGet(this, _dwellF)) {
                // throw->catch
                sourceGen = gen;
                sourceT = 0;
                targetT = 1 - __classPrivateFieldGet(this, _dwellF);
            }
            else {
                sourceGen = gen + 1;
                sourceT = 1 - __classPrivateFieldGet(this, _dwellF);
                targetT = 1;
                sourceThrow = false;
            }
        }
        else {
            // ShiftF - 1: throw (cycle C-1)
            // ShiftF - DwellF: catch (cycle C-1)
            // ShiftF: throw (cycle C)
            // ShiftF - DwellF + 1: catch
            // ShiftF + 1: throw (cycle C+1)
            if (t < __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF)) {
                // throw->catch
                sourceGen = gen - 2;
                sourceT = __classPrivateFieldGet(this, _shiftF) - 1;
                targetT = __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF);
            }
            else if (t < __classPrivateFieldGet(this, _shiftF)) {
                // catch->throw
                sourceGen = gen - 1;
                sourceT = __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF);
                targetT = __classPrivateFieldGet(this, _shiftF);
                sourceThrow = false;
            }
            else if (t < __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF) + 1) {
                // throw->catch
                sourceGen = gen;
                sourceT = __classPrivateFieldGet(this, _shiftF);
                targetT = __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF) + 1;
            }
            else {
                // catch->throw
                sourceGen = gen + 1;
                sourceT = __classPrivateFieldGet(this, _shiftF) - __classPrivateFieldGet(this, _dwellF) + 1;
                targetT = __classPrivateFieldGet(this, _shiftF) + 1;
                sourceThrow = false;
            }
        }
        const sourceGlobalVelo = this.getHandVelocity(sourceGen + (leading ? 0 : 1) + (sourceThrow ? 0 : 1), j, h, sourceThrow);
        const targetGlobalVelo = this.getHandVelocity(sourceGen + (leading ? 1 : 2) + (sourceThrow ? 0 : 0), j, h, !sourceThrow);
        const sourceLocalVelo = __classPrivateFieldGet(this, _scene).localizeVelocity(j, sourceGlobalVelo);
        const targetLocalVelo = __classPrivateFieldGet(this, _scene).localizeVelocity(j, targetGlobalVelo);
        const sourcePos = __classPrivateFieldGet(this, _handMovement).getPosition(sourceGen, j, h);
        const targetPos = __classPrivateFieldGet(this, _handMovement).getPosition(sourceGen + 1, j, h);
        const spline = new HandSpline(this.cycleTToMillis(0, sourceT), sourcePos, sourceLocalVelo, this.cycleTToMillis(0, targetT), targetPos, targetLocalVelo);
        return spline.posAt(this.cycleTToMillis(0, t));
    }
    getAbsHandPosition(gen, j, h, thr0w) {
        // gen % 2 === 0: leading, throw
        const leading = gen % 2 === 0;
        let posGen = 0;
        let t;
        if (leading) {
            if (thr0w) {
                t = 0;
                posGen = gen;
            }
            else {
                // The catch before the throw -1
                t = 1 - __classPrivateFieldGet(this, _dwellF) - 1;
                posGen = gen - 1;
            }
        }
        else {
            if (thr0w) {
                t = __classPrivateFieldGet(this, _shiftF);
                posGen = gen - 1; // Non leading, so lagging behind
            }
            else {
                // The catch before the throw -1
                t = __classPrivateFieldGet(this, _shiftF) + 1 - __classPrivateFieldGet(this, _dwellF) - 1;
                posGen = gen - 2;
            }
        }
        let pos = __classPrivateFieldGet(this, _handMovement).getPosition(posGen, j, h);
        pos = __classPrivateFieldGet(this, _scene).transformRelHandPosition(j, h, pos);
        t = this.cycleTToMillis(Math.floor(gen / 2), t);
        return [pos, t];
    }
}
_time = new WeakMap(), _scene = new WeakMap(), _cycleMgr = new WeakMap(), _handMovement = new WeakMap(), _dwellF = new WeakMap(), _shiftF = new WeakMap(), _cycleDuration = new WeakMap(), _gravity = new WeakMap();
export function isPlayableSiteswap(ss) {
    try {
        const pattern = new Parser(ss).parse();
        loopFinder(pattern);
        return pattern;
    }
    catch (e) {
        return undefined;
    }
}
